import React, {forwardRef, useImperativeHandle} from 'react';
import {bindActionCreators} from 'redux';
import {connect, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {FmlxButton} from 'fmlx-common-ui';
import {checkTrayReadiness} from 'stores/business/trayCheckingBusiness';
import {checkLoadCellError} from 'stores/business/loadCellErrorBusiness';
import withIntl from '../../../lib/withIntl';
import '../SetupWizard.scss';
import {
  handleAlignToDeckWithOffset,
  handleEjectTray,
  handleMoveToIdlePosition,
  handleProceedWellMeasurement,
  handleTouchProbeExtend,
  handleTouchProbeRetract,
} from '../../../stores/business/setupWizardBusiness/setupWizardInstrumentBusiness';
import {setWizardAllowedNext} from '../../../stores/actions/wizardStateAction';
import SetupWizardImage from '../SetupWizardImage';
import MotionControlPad from '../../PlateTypeEditor/MotionControlPad';
import SetupWizardTitleHeader from '../components/SetupWizardTitleHeader';
import {saveState} from '../../../stores/business/stateBusiness';
import {PlateDefinitionSubstep, SetupWizardStep, WizardFlag} from '../../../constants/SetupWizardEnum';
import SetupWizardGeneralStepPage from './SetupWizardGeneralStepPage';
import {showLoading} from '../../../stores/actions/appStateAction';

const Container = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
`;
const LeftPanel = styled.div.attrs({
  className: 'left-panel',
})`
  margin-right: 10px;
  border: 1.5px solid #b4b4b4;
  border-radius: 10px;
  padding: 20px;
`;

const RightPanel = styled.div.attrs({
  className: 'right-panel',
})`
  border: 1.5px solid #b4b4b4;
  border-radius: 10px;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 20px 10px;
  justify-content: space-around;
`;

const mapStateToProps = (state) => ({
  wizardFlagState: state.wizardState.wizardFlag,
  wizardViewDataState: state.wizardState.wizardViewData,
  currentWizardViewState: state.wizardState.currentWizardView,
  plateTypeEditorState: state.plateTypeEditorState,
});

const mapDispatchToProps = (dispatch) => ({
  wizardStateActionSetAllowedNext: bindActionCreators(setWizardAllowedNext, dispatch),
  setupWizardBusinessExtendTouchProbe: bindActionCreators(handleTouchProbeExtend, dispatch),
  setupWizardBusinessResetProbe: bindActionCreators(handleAlignToDeckWithOffset, dispatch),
  setupWizardBusinessProceedWellMeasurement: bindActionCreators(handleProceedWellMeasurement, dispatch),
  SWInstrumentBusinessTouchProbeRetract: bindActionCreators(handleTouchProbeRetract, dispatch),
  SWInstrumentBusinessMoveToIdle: bindActionCreators(handleMoveToIdlePosition, dispatch),
  SWInstrumentEjectTray: bindActionCreators(handleEjectTray, dispatch),
  stateBusinessSaveState: bindActionCreators(saveState, dispatch),
  appStateActionShowLoading: bindActionCreators(showLoading, dispatch),
});

// #region substep 3
function SubstepThree({wellMeasurementData}) {
  const {rowPitch, columnPitch, rowOrigin, columnOrigin, length, width, depth, height} = wellMeasurementData;

  return (
    <div className="step-plate-definition-substep-3">
      <SetupWizardTitleHeader classNameFormat="plate-definition-substep-3" intlTextFormat="plateDefinition.substep3" />
      <div className="plate-definition-image">
        <div className="value-placement">
          <span className="setup-wizard-plate-summary row-pitch">{rowPitch}</span>
          <span className="setup-wizard-plate-summary col-pitch">{columnPitch}</span>
          <span className="setup-wizard-plate-summary row-origin">{rowOrigin}</span>
          <span className="setup-wizard-plate-summary col-origin">{columnOrigin}</span>
          <span className="setup-wizard-plate-summary length">{length}</span>
          <span className="setup-wizard-plate-summary width">{width}</span>
          <span className="setup-wizard-plate-summary depth">{depth}</span>
          <span className="setup-wizard-plate-summary height">{height}</span>
        </div>
        <SetupWizardImage subStepIndex={2} stepName={SetupWizardStep.PLATE_DEFINITION} />
      </div>
    </div>
  );
}

SubstepThree.propTypes = {
  wellMeasurementData: PropTypes.object,
};

SubstepThree.defaultProps = {
  wellMeasurementData: {
    rowPitch: 0,
    columnPitch: 0,
    rowOrigin: 0,
    columnOrigin: 0,
    length: 0,
    width: 0,
    depth: 0,
    height: 0,
  },
};

// #end region substep 3

const StepSixSetupWizard = forwardRef(
  (
    {
      intl,
      setupWizardBusinessExtendTouchProbe,
      setupWizardBusinessResetProbe,
      onSaveStepData,
      setupWizardBusinessProceedWellMeasurement,
      SWInstrumentBusinessTouchProbeRetract,
      SWInstrumentBusinessMoveToIdle,
      SWInstrumentEjectTray,
      stateBusinessSaveState,
      wizardStateActionSetAllowedNext,
      wizardFlagState,
      plateTypeEditorState,
      stepData,
      appStateActionShowLoading,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const {currentSubstep} = stepData;

    const handleResetProbe = async () => {
      await setupWizardBusinessResetProbe();
    };

    const handleProceed = async () => {
      await setupWizardBusinessProceedWellMeasurement((isNoError) => {
        if (isNoError) {
          onSaveStepData({
            currentSubstep: PlateDefinitionSubstep.SUMMARY,
            processedSubstep: PlateDefinitionSubstep.SUMMARY,
            isSkippable: false,
            isStepProcessed: true,
          });
        }
        wizardStateActionSetAllowedNext(isNoError);
        stateBusinessSaveState();
      });
    };

    const retractTouchProbeMoveToIdle = async () => {
      // for seamless transition, the loading will not be hidden until the
      // handleDecrementCurrentWizardView function is finished and the step is changed
      appStateActionShowLoading();

      const retractRes = await SWInstrumentBusinessTouchProbeRetract(false);
      const moveToIdleRes = await SWInstrumentBusinessMoveToIdle();

      return {isChangeStep: moveToIdleRes && retractRes};
    };

    const onButtonClick = async () => setupWizardBusinessExtendTouchProbe();

    const onContinue = async (skipStep) => {
      const isLoadCellError = await dispatch(checkLoadCellError(null, () => onContinue(skipStep)));
      if (isLoadCellError) return null;
      if (!skipStep && currentSubstep === PlateDefinitionSubstep.GUIDE_IMAGE) {
        const {isTrayReady} = await dispatch(checkTrayReadiness());

        if (isTrayReady) {
          const result = await setupWizardBusinessResetProbe();
          return {isChangeStep: result};
        }

        return {isChangeStep: false};
      }

      if (skipStep || currentSubstep === PlateDefinitionSubstep.SUMMARY) {
        await SWInstrumentBusinessTouchProbeRetract(true);
        await SWInstrumentBusinessMoveToIdle(true);
      }

      return {};
    };

    const onBack = async () => {
      let result = retractTouchProbeMoveToIdle();

      if (currentSubstep === PlateDefinitionSubstep.GUIDE_IMAGE && wizardFlagState === WizardFlag.ERROR)
        result = await SWInstrumentEjectTray();

      return result;
    };

    useImperativeHandle(ref, () => ({
      onContinue,
      onBack,
    }));

    return (
      <>
        {currentSubstep === PlateDefinitionSubstep.GUIDE_IMAGE && (
          <SetupWizardGeneralStepPage onButtonClick={onButtonClick} />
        )}
        {currentSubstep === PlateDefinitionSubstep.WELL_MEASUREMENT && (
          <div>
            <SetupWizardTitleHeader
              classNameFormat="plateDefinition-substep-2"
              intlTextFormat="plateDefinition.substep2"
            />
            <Container>
              <LeftPanel>
                <SetupWizardImage subStepIndex={1} stepName={SetupWizardStep.PLATE_DEFINITION} />
              </LeftPanel>
              <RightPanel>
                <p className="motion-control-title">
                  {intl.formatMessage({id: 'setupWizard.step.plateDefinition.substep2.rightPanel.title'})}
                </p>
                <div className="setup-wizard__motion-control-pad">
                  <MotionControlPad style={{marginLeft: '-5px'}} moveValue={5} />
                  <span className="setup-wizard__motion-control-pad__reset-button">
                    <FmlxButton
                      variant="outline"
                      onClick={handleResetProbe}
                      label={intl.formatMessage({
                        id: 'setupWizard.step.plateDefinition.substep2.rightPanel.firstButton',
                      })}
                    />
                  </span>
                </div>
                <FmlxButton
                  label={intl.formatMessage({id: 'setupWizard.step.plateDefinition.substep2.rightPanel.secondButton'})}
                  onClick={handleProceed}
                />
              </RightPanel>
            </Container>
          </div>
        )}
        {currentSubstep === PlateDefinitionSubstep.SUMMARY && (
          <SubstepThree wellMeasurementData={plateTypeEditorState} />
        )}
      </>
    );
  },
);

StepSixSetupWizard.propTypes = {
  intl: PropTypes.any.isRequired,
  setupWizardBusinessExtendTouchProbe: PropTypes.func.isRequired,
  setupWizardBusinessResetProbe: PropTypes.func.isRequired,
  onSaveStepData: PropTypes.func.isRequired,
  setupWizardBusinessProceedWellMeasurement: PropTypes.func.isRequired,
  SWInstrumentBusinessTouchProbeRetract: PropTypes.func.isRequired,
  SWInstrumentBusinessMoveToIdle: PropTypes.func.isRequired,
  SWInstrumentEjectTray: PropTypes.func.isRequired,
  wizardStateActionSetAllowedNext: PropTypes.func.isRequired,
  stateBusinessSaveState: PropTypes.func.isRequired,
  appStateActionShowLoading: PropTypes.func.isRequired,
  wizardFlagState: PropTypes.string.isRequired,
  plateTypeEditorState: PropTypes.any.isRequired,
  stepData: PropTypes.object.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(withIntl(StepSixSetupWizard));
